import { useState, useCallback, useEffect } from "react";
import { Button } from "~/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "~/components/ui/input-otp";
import { Label } from "~/components/ui/label";
import { FaArrowRightLong } from "react-icons/fa6";
import { Input } from "~/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "~/components/ui/dialog";
import loginBirbyRightImg from "~/assets/login-birby-right.svg";
import loginGoogleImg from "~/assets/login-google.svg";
import loginFacebookImg from "~/assets/login-facebook.svg";
import termsHtml from "~/data/legal-document/terms_6.html?raw";
import privacyHtml from "~/data/legal-document/privacy.html?raw";
import { cn } from "~/lib/utils";
import { useUserStore } from "~/lib/stores/user";
import { performGoogleLogin, facebookSDK } from "~/lib/social-login";
import { ApiClientError } from "~/api/client";
import { devLog } from "~/lib/dev-log";

// ============================================================================
// Error Handling Helper
// ============================================================================

function handleAuthError(
  error: any,
  logMessage: string,
  defaultErrorMessage: string,
  onError?: (error: string) => void
) {
  devLog(logMessage, error);
  let errMsg = defaultErrorMessage;
  if (error instanceof ApiClientError && error.userMessage) {
    errMsg = error.userMessage;
  }
  onError?.(errMsg);
}

// ============================================================================
// Login Handlers - Centralized login logic
// ============================================================================

interface UseLoginHandlersProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onEmailSent?: (email: string) => void;
  invitationCode?: string;
}

function useLoginHandlers({ onSuccess, onError, onEmailSent, invitationCode }: UseLoginHandlersProps = {}) {
  const { finishDelayedSignup, emailLogin, firebaseLogin, facebookLogin } = useUserStore();

  const handleEmailSubmit = useCallback(
    async (email: string) => {
      try {
        const response = await finishDelayedSignup(email, invitationCode);
        if (response === "email-sent") {
          onEmailSent?.(email);
        } else if (response === "authenticated") {
          onSuccess?.();
        }
      } catch (error: any) {
        handleAuthError(error, "Finish delayed signup failed:", "Failed to signup. Please try again.", onError);
      }
    },
    [finishDelayedSignup, onSuccess, onError, onEmailSent, invitationCode]
  );

  const handleOTPSubmit = useCallback(
    async (email: string, otpToken: string) => {
      try {
        const response = await emailLogin(email, otpToken);
        if (response === "authenticated") {
          onSuccess?.();
        }
      } catch (error: any) {
        handleAuthError(error, "Email login failed:", "Failed to verify code. Please try again.", onError);
      }
    },
    [emailLogin, onSuccess, onError]
  );

  const handleGoogleLogin = useCallback(async () => {
    try {
      const result = await performGoogleLogin();
      const response = await firebaseLogin(result.idToken, result.email, "google", invitationCode);
      if (response === "authenticated") {
        onSuccess?.();
      }
    } catch (error: any) {
      handleAuthError(error, "Google login failed:", "Failed to sign in with Google. Please try again.", onError);
    }
  }, [firebaseLogin, onSuccess, onError, invitationCode]);

  const handleFacebookLogin = useCallback(async () => {
    try {
      const result = await facebookSDK.login();
      if (!result.userInfo.email) {
        throw new Error("Facebook account did not provide email information");
      }
      const response = await facebookLogin(result.accessToken, result.userInfo, undefined, invitationCode);
      if (response === "authenticated") {
        onSuccess?.();
      }
    } catch (error: any) {
      handleAuthError(error, "Facebook login failed:", "Failed to sign in with Facebook. Please try again.", onError);
    }
  }, [facebookLogin, onSuccess, onError, invitationCode]);

  return {
    handleEmailSubmit,
    handleOTPSubmit,
    handleGoogleLogin,
    handleFacebookLogin,
  };
}

// ============================================================================
// LoginFlow - Complete login flow with email/OTP state management
// ============================================================================

export interface LoginFlowProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  showBackButton?: boolean;
  className?: string;
  title?: string | React.ReactNode;
  subtitle?: string;
  showTerms?: boolean;
  initialEmail?: string;
  invitationCode?: string;
}

export function LoginFlow({
  onSuccess,
  onError,
  showBackButton = true,
  className,
  title = "Welcome to Blidz!",
  subtitle,
  showTerms = true,
  initialEmail,
  invitationCode,
}: LoginFlowProps) {
  const [emailSent, setEmailSent] = useState<boolean>(!!initialEmail);
  const [email, setEmail] = useState<string | undefined>(initialEmail);

  // Update state when initialEmail changes
  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
      setEmailSent(true);
    } else {
      setEmailSent(false);
      setEmail(undefined);
    }
  }, [initialEmail]);

  const handleEmailSent = useCallback((email: string) => {
    setEmail(email);
    setEmailSent(true);
  }, []);

  const handleBack = useCallback(() => {
    setEmailSent(false);
  }, []);

  return emailSent && email ? (
    <LoginOTPForm
      email={email}
      onBack={showBackButton ? handleBack : undefined}
      onSuccess={onSuccess}
      onError={onError}
      showBackButton={showBackButton}
      className={className}
    />
  ) : (
    <LoginForm
      onSuccess={onSuccess}
      onError={onError}
      onEmailSent={handleEmailSent}
      className={className}
      title={title}
      subtitle={subtitle}
      showTerms={showTerms}
      invitationCode={invitationCode}
    />
  );
}

// ============================================================================
// LoginOTPForm - Standalone OTP verification form with integrated auth
// ============================================================================

export interface LoginOTPFormProps {
  email: string;
  onBack?: () => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  showBackButton?: boolean;
  className?: string;
}

export function LoginOTPForm({
  email,
  onBack,
  onSuccess,
  onError,
  showBackButton = true,
  className,
}: LoginOTPFormProps) {
  const [otpToken, setOtpToken] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [otpError, setOtpError] = useState<string>("");

  // Use integrated login handlers
  const { handleOTPSubmit } = useLoginHandlers({ onSuccess, onError });

  const validateOTP = (otp: string): boolean => {
    if (otp.length === 0) {
      setOtpError("Verification code is required");
      return false;
    }
    if (otp.length !== 6) {
      setOtpError("Please enter the complete 6-digit code");
      return false;
    }
    if (!/^\d{6}$/.test(otp)) {
      setOtpError("Verification code must contain only numbers");
      return false;
    }
    setOtpError("");
    return true;
  };

  const handleInput = (value: string) => {
    setOtpToken(value);
    if (otpError) {
      validateOTP(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateOTP(otpToken)) {
      return;
    }

    setIsLoading(true);
    try {
      await handleOTPSubmit(email, otpToken);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("flex flex-col items-center justify-center", className)}>
      <div className="flex max-h-[673px] max-w-[573px] flex-col items-center justify-center gap-[30px] rounded-[16px] bg-[#FFFFFF] p-[16px] lg:h-[673px] lg:w-[573px] lg:gap-[60px] lg:px-[70px] lg:py-[86px]">
        <img
          src={loginBirbyRightImg}
          alt="Birby Logo"
          className="max-h-[146px] max-w-[146px] lg:h-[146px] lg:w-[146px]"
        />
        <div className="flex flex-col items-center justify-center">
          <div className="text-center text-[24px]/[28px] font-[700] text-[#171717] lg:text-[30px]/[34px]">
            Verify your email address
          </div>
          <p className="mt-[8px] text-center text-[12px]/[18px] font-[500] text-[#525252] lg:text-[14px]/[20px]">
            A six-digit code was emailed to <span className="font-semibold">{email}</span>. Input it below to verify
            your email login.
          </p>
          <div className="h-[25px] max-sm:hidden" />
          <form onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="otp-input" className="text-[12px]/[18px] font-[500] text-[#0F172A] lg:text-[14px]/[20px]">
                Verification code
              </Label>
              <div
                className={cn(
                  "mt-[6px]",
                  otpError ? "rounded-[8px] border border-red-500 hover:border-red-600" : "hover:border-gray-400"
                )}
              >
                <InputOTP maxLength={6} id="otp-input" onChange={handleInput} autoFocus disabled={isLoading}>
                  <InputOTPGroup className="flex flex-row gap-[12px] lg:gap-[16px]">
                    <InputOTPSlot
                      index={0}
                      className="max-h-[60px] max-w-[60px] transition-colors lg:h-[60px] lg:w-[60px]"
                    />
                    <InputOTPSlot
                      index={1}
                      className="max-h-[60px] max-w-[60px] transition-colors lg:h-[60px] lg:w-[60px]"
                    />
                    <InputOTPSlot
                      index={2}
                      className="max-h-[60px] max-w-[60px] transition-colors lg:h-[60px] lg:w-[60px]"
                    />
                    <InputOTPSlot
                      index={3}
                      className="max-h-[60px] max-w-[60px] transition-colors lg:h-[60px] lg:w-[60px]"
                    />
                    <InputOTPSlot
                      index={4}
                      className="max-h-[60px] max-w-[60px] transition-colors lg:h-[60px] lg:w-[60px]"
                    />
                    <InputOTPSlot
                      index={5}
                      className="max-h-[60px] max-w-[60px] transition-colors lg:h-[60px] lg:w-[60px]"
                    />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {otpError && <p className="mt-2 text-sm text-red-500">{otpError}</p>}
            </div>
            <div className="h-[25px] max-sm:hidden" />
            <Button
              variant="yellow"
              size="lg"
              type="submit"
              disabled={isLoading || !!otpError}
              className="h-[40px] w-full rounded-[16px] text-[12px]/[18px] font-[600] lg:text-[14px]/[20px]"
            >
              {isLoading ? "Verifying..." : "Continue"}
              {!isLoading && <FaArrowRightLong />}
            </Button>

            {showBackButton && onBack && (
              <>
                <div className="h-[12px]" />
                <Button
                  variant="outline"
                  size="lg"
                  type="button"
                  onClick={onBack}
                  disabled={isLoading}
                  className="h-[40px] w-full rounded-[16px] text-[12px]/[18px] font-[600] lg:text-[14px]/[20px]"
                >
                  Back
                </Button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// LoginForm - Standalone login form with integrated auth
// ============================================================================

export interface LoginFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onEmailSent?: (email: string) => void;
  className?: string;
  title?: string | React.ReactNode;
  subtitle?: string;
  showTerms?: boolean;
  invitationCode?: string;
}

export function LoginForm({
  onSuccess,
  onError,
  onEmailSent,
  className,
  title = "Welcome to Blidz!",
  subtitle,
  showTerms = true,
  invitationCode,
}: LoginFormProps) {
  const [email, setEmail] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>("");

  // Use integrated login handlers
  const { handleEmailSubmit, handleGoogleLogin, handleFacebookLogin } = useLoginHandlers({
    onSuccess,
    onError,
    onEmailSent,
    invitationCode,
  });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setEmailError("Email is required");
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      validateEmail(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      return;
    }

    setIsLoading(true);
    try {
      await handleEmailSubmit(email);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLoginClick = async () => {
    setIsLoading(true);
    try {
      await handleGoogleLogin();
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookLoginClick = async () => {
    setIsLoading(true);
    try {
      await handleFacebookLogin();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("w-full space-y-4", className)}>
      {(title || subtitle) && (
        <div className="text-center">
          {title &&
            (typeof title === "string" ? (
              <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">{title}</h2>
            ) : (
              <div>{title}</div>
            ))}
          {subtitle && <p className="mt-2 text-sm text-gray-600">{subtitle}</p>}
        </div>
      )}

      <div className="space-y-3">
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="h-12 w-full rounded-2xl"
          onClick={handleGoogleLoginClick}
          disabled={isLoading}
        >
          <img src={loginGoogleImg} alt="Google" className="mr-3 h-5 w-5" />
          <span className="text-sm font-semibold">Continue with Google</span>
        </Button>

        <Button
          type="button"
          variant="outline"
          size="lg"
          className="h-12 w-full rounded-2xl"
          onClick={handleFacebookLoginClick}
          disabled={isLoading}
        >
          <img src={loginFacebookImg} alt="Facebook" className="mr-3 h-5 w-5" />
          <span className="text-sm font-semibold">Continue with Facebook</span>
        </Button>
      </div>

      <div className="flex items-center">
        <div className="flex-1 border-t border-gray-200" />
        <span className="bg-white px-4 text-sm text-gray-500">OR</span>
        <div className="flex-1 border-t border-gray-200" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={handleInput}
            disabled={isLoading}
            className={cn(
              "h-12 rounded-2xl transition-colors",
              emailError ? "border-red-500 focus:border-red-500" : ""
            )}
          />
          {emailError && <p className="mt-1 text-sm text-red-500">{emailError}</p>}
        </div>

        <Button
          variant="yellow"
          size="lg"
          type="submit"
          disabled={isLoading || !!emailError || !email.trim()}
          className="h-12 w-full rounded-2xl text-sm font-semibold"
        >
          {isLoading ? "Sending..." : "Continue"}
          {!isLoading && <FaArrowRightLong className="ml-2" />}
        </Button>
      </form>

      {showTerms && (
        <div className="text-center text-xs text-gray-600">
          By continuing to sign in, you agree to our{" "}
          <Dialog>
            <DialogTrigger asChild>
              <button className="underline transition-colors hover:cursor-pointer hover:text-black">Terms</button>
            </DialogTrigger>
            <DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Terms and Conditions</DialogTitle>
              </DialogHeader>
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: termsHtml }} />
            </DialogContent>
          </Dialog>{" "}
          and acknowledge our{" "}
          <Dialog>
            <DialogTrigger asChild>
              <button className="underline transition-colors hover:cursor-pointer hover:text-black">
                Privacy Policy
              </button>
            </DialogTrigger>
            <DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Privacy Policy</DialogTitle>
              </DialogHeader>
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: privacyHtml }} />
            </DialogContent>
          </Dialog>
          .
        </div>
      )}
    </div>
  );
}
